import { useState, useEffect, useCallback } from 'react'
import { useNavigate } from 'react-router-dom'
import { clsx } from 'clsx'
import { useAuth } from '../hooks/useAuth'
import { useAuthStore } from '../stores/authStore'
import { useDevMode, getAllowedLevels } from '../hooks/useDevMode'
import { useChatStore } from '../stores/chatStore'
import { getPreferences, updatePreferences } from '../api/preferences'
import TherapistAvatar from '../components/chat/TherapistAvatar'
import BugReportModal from '../components/settings/BugReportModal'
import Toggle from '../components/ui/Toggle'
import { usePWAInstall } from '../hooks/usePWAInstall'

const apiUrl = import.meta.env.VITE_API_URL || ''

function ConfigNumberInput({
  label,
  hint,
  value,
  onChange,
  min,
  max,
  step = 1,
}: {
  label: string
  hint?: string
  value: number
  onChange: (v: number) => void
  min: number
  max: number
  step?: number
}) {
  return (
    <div className="flex items-center justify-between gap-2">
      <div className="flex-1 min-w-0">
        <span className="text-xs text-neutral-500 font-mono">{label}</span>
        {hint && <p className="text-[10px] text-neutral-400 leading-tight">{hint}</p>}
      </div>
      <input
        type="number"
        value={value}
        min={min}
        max={max}
        step={step}
        onChange={(e) => onChange(Number(e.target.value))}
        className="w-20 text-xs font-mono text-neutral-800 text-right bg-white border border-neutral-200 rounded px-2 py-1 focus:border-amber-400 focus:outline-none flex-shrink-0"
      />
    </div>
  )
}

function ConfigToggleRow({
  label,
  hint,
  value,
  onChange,
}: {
  label: string
  hint?: string
  value: boolean
  onChange: (v: boolean) => void
}) {
  return (
    <div className="flex items-center justify-between gap-2">
      <div className="flex-1 min-w-0">
        <span className="text-xs text-neutral-500 font-mono">{label}</span>
        {hint && <p className="text-[10px] text-neutral-400 leading-tight">{hint}</p>}
      </div>
      <button
        onClick={() => onChange(!value)}
        className={clsx(
          'w-9 h-5 rounded-full relative transition-colors duration-200',
          value ? 'bg-amber-500' : 'bg-neutral-300'
        )}
      >
        <span
          className={clsx(
            'absolute top-0.5 left-0.5 w-4 h-4 bg-white rounded-full shadow-sm transition-transform duration-200',
            value ? 'translate-x-4' : 'translate-x-0'
          )}
        />
      </button>
    </div>
  )
}

interface ResponseFlowConfig {
  text_only_until: number
  voice_starts_at: number
  video_starts_at: number
  emotional_threshold_for_video: number
  lipsync_audio_speed: number
  send_portrait_with_text: boolean
  voice_enabled_by_default: boolean
  video_enabled_by_default: boolean
  message_logging_enabled: boolean
  portrait_size: string
  log_file_path: string
}

export default function SettingsPage() {
  const navigate = useNavigate()
  const { logout } = useAuth()
  const user = useAuthStore((state) => state.user)
  const { debugLevel, setDebugLevel, canToggleDebug, role } = useDevMode()
  const playbackRate = useChatStore((s) => s.playbackRate)
  const { canInstall, isInstalled, install } = usePWAInstall()

  // Preferences synced with backend
  const [voiceEnabled, setVoiceEnabledLocal] = useState(true)
  const [videoEnabled, setVideoEnabledLocal] = useState(false)
  const [selectedTherapist, setSelectedTherapistLocal] = useState<'auto' | 'wendy' | 'artem' | 'una'>('auto')
  const [version, setVersion] = useState('')
  const [bugReportOpen, setBugReportOpen] = useState(false)
  const [flowConfig, setFlowConfig] = useState<ResponseFlowConfig | null>(null)
  const [configSaving, setConfigSaving] = useState(false)
  const [configSaved, setConfigSaved] = useState(false)

  // Load preferences from backend
  useEffect(() => {
    const loadPrefs = async () => {
      try {
        const prefs = await getPreferences()
        setVoiceEnabledLocal(prefs.voice_enabled ?? true)
        setVideoEnabledLocal(prefs.video_enabled ?? false)
        if (prefs.playback_rate !== null) {
          useChatStore.getState().setPlaybackRate(prefs.playback_rate as 1 | 1.25 | 1.5)
        }
        if (prefs.therapist_preference) {
          setSelectedTherapistLocal(prefs.therapist_preference as 'wendy' | 'artem' | 'una')
        }
      } catch {
        // Use defaults on error
      }
    }
    loadPrefs()
  }, [])

  // Save preference to backend (fire and forget)
  const savePreference = useCallback((data: Record<string, unknown>) => {
    updatePreferences(data).catch(() => {/* ignore save errors */})
  }, [])

  const setVoiceEnabled = useCallback((enabled: boolean) => {
    setVoiceEnabledLocal(enabled)
    savePreference({ voice_enabled: enabled })
  }, [savePreference])

  const setVideoEnabled = useCallback((enabled: boolean) => {
    setVideoEnabledLocal(enabled)
    savePreference({ video_enabled: enabled })
  }, [savePreference])

  const setSelectedTherapist = useCallback((therapist: 'auto' | 'wendy' | 'artem' | 'una') => {
    setSelectedTherapistLocal(therapist)
    savePreference({ therapist_preference: therapist })
  }, [savePreference])

  const saveFlowConfig = async (patch: Partial<ResponseFlowConfig>) => {
    if (!flowConfig) return
    const updated = { ...flowConfig, ...patch }
    setFlowConfig(updated)
    setConfigSaving(true)
    setConfigSaved(false)
    try {
      const response = await fetch(`${apiUrl}/api/debug/response-flow-config`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(patch),
      })
      if (response.ok) {
        const serverConfig = await response.json()
        setFlowConfig(serverConfig)
        setConfigSaved(true)
        setTimeout(() => setConfigSaved(false), 2000)
      }
    } catch {
      // Revert on error
      setFlowConfig(flowConfig)
    } finally {
      setConfigSaving(false)
    }
  }

  // Fetch version from backend
  useEffect(() => {
    const fetchVersion = async () => {
      try {
        const response = await fetch(`${apiUrl}/api/health`)
        if (response.ok) {
          const data = await response.json()
          setVersion(data.version || '')
        }
      } catch {
        // Ignore errors
      }
    }
    fetchVersion()
  }, [])

  // Fetch response flow config when debug mode is active
  useEffect(() => {
    if (debugLevel === 'off') {
      setFlowConfig(null)
      return
    }
    const fetchConfig = async () => {
      try {
        const response = await fetch(`${apiUrl}/api/debug/response-flow-config`)
        if (response.ok) {
          setFlowConfig(await response.json())
        }
      } catch {
        // Ignore errors
      }
    }
    fetchConfig()
  }, [debugLevel])

  const therapistOptions = [
    {
      id: 'auto' as const,
      name: 'Автоматически',
      description: 'Система выберет подходящего терапевта',
      icon: (
        <div className="w-10 h-10 rounded-full bg-gradient-to-br from-primary-100 to-secondary-100 flex items-center justify-center">
          <svg className="w-5 h-5 text-primary-600" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" />
          </svg>
        </div>
      ),
    },
    {
      id: 'wendy' as const,
      name: 'Венди',
      description: 'Поддерживающий, эмпатичный стиль',
      icon: <TherapistAvatar therapist={{ type: 'wendy', name: 'Венди' }} size="md" />,
    },
    {
      id: 'artem' as const,
      name: 'Артём',
      description: 'Провокативный, прямой стиль',
      icon: <TherapistAvatar therapist={{ type: 'artem', name: 'Артём' }} size="md" />,
    },
    {
      id: 'una' as const,
      name: 'Уна',
      description: 'Неформальный, флирт и провокации',
      icon: <TherapistAvatar therapist={{ type: 'una', name: 'Уна' }} size="md" />,
    },
  ]

  return (
    <div className="min-h-screen bg-gradient-calm">
      {/* Header */}
      <header className="glass border-b border-neutral-200/50 sticky top-0 z-10 safe-area-pt">
        <div className="max-w-2xl mx-auto px-4 py-4 flex items-center">
          <button
            aria-label="Назад"
            onClick={() => navigate('/chat')}
            className="p-2 -ml-2 hover:bg-neutral-100 rounded-full transition-colors"
          >
            <svg className="w-6 h-6 text-neutral-600" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M15 19l-7-7 7-7" />
            </svg>
          </button>
          <h1 className="ml-2 text-lg font-semibold text-neutral-800">Настройки</h1>
        </div>
      </header>

      <div className="max-w-2xl mx-auto px-4 py-6 space-y-6 animate-fade-in safe-area-pb">
        {/* Profile Section */}
        <section className="card">
          <h2 className="text-xs font-medium text-neutral-600 uppercase tracking-wider mb-4">Профиль</h2>

          <div className="flex items-center gap-4">
            <div className="w-16 h-16 bg-gradient-to-br from-primary-400 to-primary-600 rounded-2xl flex items-center justify-center shadow-primary">
              <span className="text-white font-bold text-2xl">
                {(user?.displayName || user?.email || 'U')[0].toUpperCase()}
              </span>
            </div>
            <div className="flex-1 min-w-0">
              <p className="font-semibold text-neutral-800 text-lg truncate">
                {user?.displayName || 'Пользователь'}
              </p>
              <p className="text-sm text-neutral-500 truncate">{user?.email || 'Email не указан'}</p>
            </div>
            <button className="btn-ghost text-sm">
              Изменить
            </button>
          </div>

          {user?.telegramLinked && (
            <div className="mt-4 pt-4 border-t border-neutral-100">
              <div className="flex items-center gap-2 text-sm text-success-dark">
                <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm4.64 6.8c-.15 1.58-.8 5.42-1.13 7.19-.14.75-.42 1-.68 1.03-.58.05-1.02-.38-1.58-.75-.88-.58-1.38-.94-2.23-1.5-.99-.65-.35-1.01.22-1.59.15-.15 2.71-2.48 2.76-2.69a.2.2 0 00-.05-.18c-.06-.05-.14-.03-.21-.02-.09.02-1.49.95-4.22 2.79-.4.27-.76.41-1.08.4-.36-.01-1.04-.2-1.55-.37-.63-.2-1.12-.31-1.08-.66.02-.18.27-.36.74-.55 2.92-1.27 4.86-2.11 5.83-2.51 2.78-1.16 3.35-1.36 3.73-1.36.08 0 .27.02.39.12.1.08.13.19.14.27-.01.06.01.24 0 .38z"/>
                </svg>
                <span>Telegram привязан</span>
              </div>
            </div>
          )}

          {!user?.telegramLinked && (
            <button className="mt-4 w-full btn bg-[#0088cc] hover:bg-[#0077b5] text-white text-sm">
              <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm4.64 6.8c-.15 1.58-.8 5.42-1.13 7.19-.14.75-.42 1-.68 1.03-.58.05-1.02-.38-1.58-.75-.88-.58-1.38-.94-2.23-1.5-.99-.65-.35-1.01.22-1.59.15-.15 2.71-2.48 2.76-2.69a.2.2 0 00-.05-.18c-.06-.05-.14-.03-.21-.02-.09.02-1.49.95-4.22 2.79-.4.27-.76.41-1.08.4-.36-.01-1.04-.2-1.55-.37-.63-.2-1.12-.31-1.08-.66.02-.18.27-.36.74-.55 2.92-1.27 4.86-2.11 5.83-2.51 2.78-1.16 3.35-1.36 3.73-1.36.08 0 .27.02.39.12.1.08.13.19.14.27-.01.06.01.24 0 .38z"/>
              </svg>
              Привязать Telegram
            </button>
          )}
        </section>

        {/* Media Preferences */}
        <section className="card">
          <h2 className="text-xs font-medium text-neutral-600 uppercase tracking-wider mb-4">Медиа</h2>

          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="font-medium text-neutral-800">Голосовые ответы</p>
                <p className="text-sm text-neutral-500">Терапевт будет отвечать голосом</p>
              </div>
              <Toggle label="Голосовые ответы" enabled={voiceEnabled} onChange={setVoiceEnabled} />
            </div>

            <div className="border-t border-neutral-100" />

            <div className="flex items-center justify-between">
              <div>
                <p className="font-medium text-neutral-800">Видео-ответы</p>
                <p className="text-sm text-neutral-500">Реалистичные видео с терапевтом</p>
              </div>
              <Toggle label="Видео-ответы" enabled={videoEnabled} onChange={setVideoEnabled} />
            </div>

            {videoEnabled && (
              <div className="bg-warning-light/50 rounded-xl p-3 text-sm text-warning-dark">
                Видео-ответы потребляют больше трафика и времени генерации
              </div>
            )}
          </div>
        </section>

        {/* Therapist Preference */}
        <section className="card">
          <h2 className="text-xs font-medium text-neutral-600 uppercase tracking-wider mb-4">Терапевт по умолчанию</h2>

          <div className="space-y-3">
            {therapistOptions.map((option) => (
              <button
                key={option.id}
                onClick={() => setSelectedTherapist(option.id)}
                className={clsx(
                  'w-full flex items-center gap-4 p-4 rounded-2xl border-2 transition-all text-left',
                  selectedTherapist === option.id
                    ? option.id === 'wendy'
                      ? 'border-wendy bg-wendy-light/30'
                      : option.id === 'artem'
                        ? 'border-artem bg-artem-light/30'
                        : option.id === 'una'
                          ? 'border-una bg-una/10'
                          : 'border-primary-500 bg-primary-50'
                    : 'border-neutral-200 hover:border-neutral-300'
                )}
              >
                {option.icon}
                <div className="flex-1">
                  <p className="font-medium text-neutral-800">{option.name}</p>
                  <p className="text-sm text-neutral-500">{option.description}</p>
                </div>
                <div
                  className={clsx(
                    'w-6 h-6 rounded-full border-2 flex items-center justify-center transition-all',
                    selectedTherapist === option.id
                      ? option.id === 'wendy'
                        ? 'border-wendy bg-wendy'
                        : option.id === 'artem'
                          ? 'border-artem bg-artem'
                          : option.id === 'una'
                            ? 'border-una bg-una'
                            : 'border-primary-500 bg-primary-500'
                      : 'border-neutral-300'
                  )}
                >
                  {selectedTherapist === option.id && (
                    <svg className="w-4 h-4 text-white" fill="currentColor" viewBox="0 0 20 20">
                      <path
                        fillRule="evenodd"
                        d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                        clipRule="evenodd"
                      />
                    </svg>
                  )}
                </div>
              </button>
            ))}
          </div>
        </section>

        {/* Install App — only shown when PWA install is available */}
        {canInstall && (
          <section className="card border border-sky-200 bg-sky-50/30">
            <button
              onClick={install}
              className="w-full flex items-center gap-4 p-2"
            >
              <div className="w-10 h-10 rounded-full bg-sky-100 flex items-center justify-center flex-shrink-0">
                <svg className="w-5 h-5 text-sky-600" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
                </svg>
              </div>
              <div className="flex-1 text-left">
                <p className="font-medium text-neutral-800">Установить приложение</p>
                <p className="text-sm text-neutral-500">Быстрый доступ с домашнего экрана</p>
              </div>
            </button>
          </section>
        )}

        {isInstalled && (
          <section className="card">
            <div className="flex items-center gap-3 p-2 text-sm text-green-700">
              <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
              </svg>
              Приложение установлено
            </div>
          </section>
        )}

        {/* Quick Links */}
        <section className="card">
          <h2 className="text-xs font-medium text-neutral-600 uppercase tracking-wider mb-4">Быстрые ссылки</h2>

          <div className="space-y-3">
            <button
              onClick={() => navigate('/goals')}
              className="w-full flex items-center justify-between p-3 hover:bg-neutral-50 rounded-xl transition-colors"
            >
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-full bg-primary-100 flex items-center justify-center">
                  <svg className="w-5 h-5 text-primary-600" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                </div>
                <span className="font-medium text-neutral-800">Мои цели</span>
              </div>
              <svg className="w-5 h-5 text-neutral-500" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" />
              </svg>
            </button>

            <button
              onClick={() => navigate('/history')}
              className="w-full flex items-center justify-between p-3 hover:bg-neutral-50 rounded-xl transition-colors"
            >
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-full bg-secondary-100 flex items-center justify-center">
                  <svg className="w-5 h-5 text-secondary-600" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                </div>
                <span className="font-medium text-neutral-800">История сессий</span>
              </div>
              <svg className="w-5 h-5 text-neutral-500" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" />
              </svg>
            </button>
          </div>
        </section>

        {/* Data & Privacy */}
        <section className="card">
          <h2 className="text-xs font-medium text-neutral-600 uppercase tracking-wider mb-4">Данные и конфиденциальность</h2>

          <div className="space-y-3">
            <button className="w-full flex items-center justify-between p-3 hover:bg-neutral-50 rounded-xl transition-colors">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-full bg-neutral-100 flex items-center justify-center">
                  <svg className="w-5 h-5 text-neutral-600" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                  </svg>
                </div>
                <span className="font-medium text-neutral-800">Экспорт данных</span>
              </div>
              <svg className="w-5 h-5 text-neutral-500" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" />
              </svg>
            </button>

            <button className="w-full flex items-center justify-between p-3 hover:bg-neutral-50 rounded-xl transition-colors">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-full bg-neutral-100 flex items-center justify-center">
                  <svg className="w-5 h-5 text-neutral-600" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
                  </svg>
                </div>
                <span className="font-medium text-neutral-800">Политика конфиденциальности</span>
              </div>
              <svg className="w-5 h-5 text-neutral-500" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" />
              </svg>
            </button>

            <button
              onClick={() => setBugReportOpen(true)}
              className="w-full flex items-center justify-between p-3 hover:bg-neutral-50 rounded-xl transition-colors"
              data-testid="bug-report-button"
            >
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-full bg-warning-light flex items-center justify-center">
                  <svg className="w-5 h-5 text-warning-dark" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                  </svg>
                </div>
                <span className="font-medium text-neutral-800">Сообщить об ошибке</span>
              </div>
              <svg className="w-5 h-5 text-neutral-500" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" />
              </svg>
            </button>
          </div>
        </section>

        {/* Debug Settings — visible for admin/clinician (canToggleDebug) */}
        {canToggleDebug && (
          <section className="card border border-amber-200 bg-amber-50/30">
            <h2 className="text-xs font-medium text-amber-700 uppercase tracking-wider mb-4">Debug</h2>

            {/* Debug Level Selector */}
            <div className="mb-4">
              <p className="text-sm font-medium text-neutral-700 mb-1">Debug Level</p>
              <p className="text-[10px] text-neutral-400 mb-2">OFF — нет отладки. BASIC — агенты + эмоции. ADVANCED — WS, логи, pipeline, метрики.</p>
              <div className="flex gap-2">
                {getAllowedLevels(role).map((level) => (
                  <button
                    key={level}
                    onClick={() => setDebugLevel(level)}
                    className={clsx(
                      'px-3 py-1.5 rounded-lg text-sm font-medium transition-colors',
                      debugLevel === level
                        ? 'bg-amber-500 text-white'
                        : 'bg-white text-neutral-600 border border-neutral-200 hover:border-amber-300'
                    )}
                  >
                    {level.toUpperCase()}
                  </button>
                ))}
              </div>
            </div>

            {/* Playback Speed */}
            <div className="flex items-center justify-between py-2 border-t border-amber-100">
              <div>
                <span className="text-sm text-neutral-600">Playback Speed</span>
                <p className="text-[10px] text-neutral-400">Скорость воспроизведения аудио/видео. Меняется в шапке чата.</p>
              </div>
              <span className="text-sm font-mono text-neutral-800">{playbackRate}x</span>
            </div>

            {/* Response Flow Config — editable */}
            {flowConfig && (
              <div className="pt-2 border-t border-amber-100">
                <div className="flex items-center justify-between mb-2">
                  <p className="text-xs font-medium text-amber-700 uppercase tracking-wider">Response Flow Config</p>
                  {configSaving && <span className="text-xs text-amber-500">Saving...</span>}
                  {configSaved && <span className="text-xs text-green-600">Saved</span>}
                </div>
                <div className="space-y-2">
                  <ConfigNumberInput
                    label="voice_starts_at"
                    hint="С какого сообщения сессии включать голос"
                    value={flowConfig.voice_starts_at}
                    onChange={(v) => saveFlowConfig({ voice_starts_at: v })}
                    min={1} max={20}
                  />
                  <ConfigNumberInput
                    label="video_starts_at"
                    hint="С какого сообщения сессии включать видео"
                    value={flowConfig.video_starts_at}
                    onChange={(v) => saveFlowConfig({ video_starts_at: v })}
                    min={1} max={20}
                  />
                  <ConfigNumberInput
                    label="emotional_threshold"
                    hint="Порог эмоциональности (0–1) для отправки видео"
                    value={flowConfig.emotional_threshold_for_video}
                    onChange={(v) => saveFlowConfig({ emotional_threshold_for_video: v })}
                    min={0} max={1} step={0.05}
                  />
                  <ConfigNumberInput
                    label="lipsync_audio_speed"
                    hint="Скорость аудио для lipsync-видео (1.0 = нормальная)"
                    value={flowConfig.lipsync_audio_speed}
                    onChange={(v) => saveFlowConfig({ lipsync_audio_speed: v })}
                    min={0.5} max={2.0} step={0.05}
                  />
                  <ConfigToggleRow
                    label="voice_enabled_default"
                    hint="Голос включён для новых пользователей"
                    value={flowConfig.voice_enabled_by_default}
                    onChange={(v) => saveFlowConfig({ voice_enabled_by_default: v })}
                  />
                  <ConfigToggleRow
                    label="video_enabled_default"
                    hint="Видео включено для новых пользователей"
                    value={flowConfig.video_enabled_by_default}
                    onChange={(v) => saveFlowConfig({ video_enabled_by_default: v })}
                  />
                  <ConfigToggleRow
                    label="send_portrait_with_text"
                    hint="Отправлять аватар терапевта с текстовыми ответами"
                    value={flowConfig.send_portrait_with_text}
                    onChange={(v) => saveFlowConfig({ send_portrait_with_text: v })}
                  />
                </div>
              </div>
            )}

            {!flowConfig && (
              <div className="pt-2 border-t border-amber-100">
                <p className="text-xs text-neutral-400 italic">Loading config...</p>
              </div>
            )}
          </section>
        )}

        {/* Logout */}
        <button
          onClick={logout}
          className="w-full btn bg-error-light text-error-dark hover:bg-error-light/80"
        >
          <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
          </svg>
          Выйти из аккаунта
        </button>

        {/* Version info */}
        <p className="text-center text-xs text-neutral-500 pb-4">
          Janus Therapy {version ? `v${version}` : 'Web'}
        </p>
      </div>

      {/* Bug Report Modal */}
      {bugReportOpen && (
        <BugReportModal
          onClose={() => setBugReportOpen(false)}
          onSuccess={() => {
            // Could show a toast notification here
          }}
        />
      )}
    </div>
  )
}
